// 详情页外壳与版块：头部（返回 / 标题 / 状态 / 全屏查看 / 在老版中操作）+ 主区 + 右侧信息栏。
// 规格照订单 OrderDetail；按容器宽度排版（iPad 分栏、分屏时可用宽度只剩 320–507）。建议主会话提取到 src/components。
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { Icon } from '@/components/Icon';
import { ErrorState, Skeleton } from '@/components/States';
import s from './Detail.module.css';

type ShellProps = {
  embedded?: boolean;
  /** 没有历史可退时回到哪里 */
  backTo: string;
  title: ReactNode;
  badges?: ReactNode;
  /** iPad 分栏里显示「全屏查看」 */
  openHref?: string;
  /** 写操作一律去老版（只读体验期） */
  legacy?: string;
  actions?: ReactNode;
  footer?: ReactNode;
  sub?: ReactNode;
  main: ReactNode;
  aside?: ReactNode;
};

export function DetailShell({ embedded, backTo, title, badges, openHref, legacy, actions, footer, sub, main, aside }: ShellProps) {
  const ff = useFormFactor();
  const nav = useNavigate();
  const wide = !embedded && ff !== 'phone' && !!aside;
  return (
    <div className={`${s.page} ${embedded ? s.embedded : ''} ${ff === 'phone' ? s.phone : ''}`}>
      <header className={s.head}>
        {!embedded && (
          <button type="button" className={s.back} onClick={() => (history.length > 1 ? nav(-1) : nav(backTo))} aria-label="返回"><Icon name="back" size={20} /></button>
        )}
        <h1>{title}</h1>
        {badges}
        <span className={s.sp} />
        {embedded && openHref && <Link className={s.open} to={openHref}>全屏查看</Link>}
        {actions}
        {legacy && <a className={s.legacy} href={legacy}>在老版中操作</a>}
      </header>
      <div className={s.scroll}>
        {sub && <p className={s.sub}>{sub}</p>}
        {wide ? (
          <div className={s.grid}><div className={s.col}>{main}</div><aside className={s.col}>{aside}</aside></div>
        ) : (
          <div className={s.col}>{main}{aside}</div>
        )}
      </div>
      {footer}
    </div>
  );
}

export function DetailLoading() {
  return <div className={s.loading}><Skeleton /><Skeleton lines={6} /></div>;
}

export function DetailError({ title, error, path, onRetry }: { title: string; error: Error; path: string; onRetry: () => void }) {
  return <ErrorState title={title} message={error.message} detail={`GET ${path}`} onRetry={onRetry} />;
}

/** 版块卡：标题行可带标记（「瓶」「箱」）、说明、右侧入口 */
export function Panel({ title, mark, note, extra, flush, children }: { title: ReactNode; mark?: ReactNode; note?: ReactNode; extra?: ReactNode; flush?: boolean; children: ReactNode }) {
  return (
    <section className={s.panel}>
      <h3>{mark}<span className={s.pt}>{title}</span>{note && <small>{note}</small>}{extra && <em>{extra}</em>}</h3>
      {children != null && children !== false && <div className={flush ? s.flush : s.pb}>{children}</div>}
    </section>
  );
}

/** 标签 / 值：值为 undefined 时整行不出现（服务端裁掉的字段不显示） */
export function KV({ rows }: { rows: [ReactNode, ReactNode | undefined][] }) {
  return (
    <dl className={s.kv}>
      {rows.filter(([, v]) => v !== undefined).map(([k, v], i) => (
        <div key={i}><dt>{k}</dt><dd>{v === null || v === '' ? <span className="em">—</span> : v}</dd></div>
      ))}
    </dl>
  );
}

export function Kpis({ items, className }: { items: { label: string; value: ReactNode; unit?: string; tone?: 'neg' | 'hl' }[]; className?: string }) {
  return (
    <div className={`${s.kpis} ${className || ''}`} style={{ ['--n' as string]: items.length }}>
      {items.map((k) => (
        <div key={k.label}>
          <span>{k.label}</span>
          <b className={`tnum ${k.tone === 'neg' ? 'neg' : k.tone === 'hl' ? s.hl : ''}`}>{k.value}{k.unit && <small> {k.unit}</small>}</b>
        </div>
      ))}
    </div>
  );
}

export { s as detailStyles };
